/**
 * useAppRouting — the page-routing logic seam (handleNavigate + handlePopState).
 *
 * TS-060 T4 slice 6 (DEC-COUNCIL-015): `handleNavigate` (App.jsx v5.46.76
 * base 72848712 lines 638-894; pre-move tree lines 634-890) and the
 * `handlePopState` handler BODY (base 1464-1545; pre-move tree lines
 * 1462-1537, previously defined INLINE inside the popstate registration
 * effect) moved VERBATIM behind a deps object. The hook owns NO routing
 * state and registers NO effects — every state value, setter, ref, constant
 * and helper threads in from App.jsx per the useQuoteEditor pattern.
 *
 * ALL FOUR routing effects STAY IN APP.JSX at their exact positions
 * (binding design decision, T4): the initial URL→page sync effect (deps
 * [user]), the currentPage/stage→history.pushState effect, the mount-only
 * replaceState seed, and the popstate REGISTRATION effect (now
 * registration-only: addEventListener/cleanup + deps [currentPage, stage]).
 * Moving them would reorder React's effect sequence relative to the
 * ref-mirror and AI-trigger effects — the pipeline entanglement the T4
 * finding warned about. `PAGE_ORDER`, `QM_STATE_KEY` and
 * `readQmStateFromStorage` also STAY in App (test-pinned there) and thread
 * in as deps.
 *
 * Identity contract: both returned closures are deliberately re-created
 * every render (NO useCallback/useMemo) exactly like the originals. The
 * popstate registration effect in App re-subscribes only on
 * [currentPage, stage], so the REGISTERED listener keeps the closure from
 * the render at which those deps last changed — identical to the original
 * inline-handler semantics (the old effect also only re-created the handler
 * when those deps changed). Closure-staleness audit: the handler body reads
 * NO App state other than currentPage/stage — everything else it touches is
 * a ref (isPopstateNav, canNavigateToQuoteStageRef), a stable setter, a
 * module import (pageToPath, resolveHistoryQuoteStageTarget), the threaded
 * PAGE_ORDER array (derived from the localStorage-backed receivables kill
 * switch, constant within a session), or the lazily-wrapped
 * openStage5FromCurrentScope — so the staleness window is unchanged.
 *
 * Module-level pure imports (pageToPath from page-routing,
 * resolveHistoryQuoteStageTarget from project-stage) are imported here
 * directly, matching the T3/T5 convention. App.jsx KEEPS its own
 * pageToPath/pathToPage import (the pushState effect and initial URL sync
 * that stay in App use them).
 *
 * TDZ note: four App bindings the bodies call are declared BELOW the
 * App.jsx instantiation point — `loadProject`, `resetForm`,
 * `syncQuoteEditorBackToState` (the DEC-COUNCIL-013 editor.syncBack alias)
 * and `openStage5FromCurrentScope` (the T5 slice-5 useStageNavigation
 * destructure). They thread as LAZY WRAPPERS keeping their original names:
 * building an arrow at deps-build time does not evaluate the binding, and
 * every call site sits inside a navigation/popstate handler body (or the
 * intake-bridge async IIFE) that runs strictly after render completes, so
 * the wrappers are runtime-equivalent to the original lazy closure reads.
 *
 * Receiver preservation: `editor.reset()` / `editor.restoreFromSession()`
 * stay property-access calls on the threaded editor object; platform
 * globals (`sessionStorage.removeItem`, `window.history.pushState`,
 * `console.warn`, `setTimeout`, `Math.*`, `String`, `Array.isArray`) are
 * kept verbatim as property-access calls — no detached aliases introduced.
 * `syncQuoteEditorBackToState` was ALREADY a detached alias in App
 * (`const syncQuoteEditorBackToState = editor.syncBack;`) and is called
 * bare exactly as before via its lazy wrapper.
 *
 * Annotation-only deltas from the raw App.jsx bytes (documented for the
 * byte-diff proof): the two closure signatures gained parameter types, the
 * intake-bridge `seedIfEmpty` helper gained `(k: string, v: unknown)`, and
 * the two `err?.message` reads in the intake-bridge catch became
 * `(err as { message?: string })?.message` per the established slice
 * convention for strict-mode catch variables (see use-scope-morph.ts /
 * use-project-summary.ts). `handlePopState` was de-indented by exactly one
 * 4-space level (it left the effect's nesting); no other bytes changed.
 *
 * Papercuts wave (2026-07-16, behavioural round 2) — two deliberate deltas
 * from the moved bytes, both in handleNavigate: (1) the same-page no-op
 * guard gained explicit parentheses around `page === currentPage &&
 * !options` (no behaviour change — && already bound tighter than ||) plus
 * a comment naming the option-bearing same-page callers the guard must let
 * through; (2) the leave-quotemaker save payload's `currentStage:
 * quoteEditorOpen ? 5 : stage` collapsed to the literal 5 — the payload is
 * only built when syncedQuote exists, which requires quoteEditorOpen, so
 * the `: stage` arm was unreachable (the App.jsx save-quote sibling
 * already hardcodes 5).
 *
 * v5.47.37 (the Run Sheet open-swallow) — the entry guard is no longer the
 * one-liner described in the papercuts note above. Because these closures are
 * deliberately per-render (see the identity contract above), a MEMOISED caller
 * pins them: openQuoteForProject's dep list omitted `loadProject`, so it pinned
 * loadProject -> completeProjectLoad -> handleNavigate from a render where
 * `currentPage` was still 'quotemaker'. completeProjectLoad calls
 * handleNavigate('quotemaker') as its LAST statement, so the same-page no-op
 * swallowed it and the operator was left on the Run Sheet with the project fully
 * loaded — and the loaded project then drifted from the row they thought they
 * had opened, which renamed the wrong quote. The guard is now two statements:
 * the same-page test reads the LIVE `currentPageRef`, and the mid-transition
 * block is exempted for a MUST-LAND navigation (`isLoadingProjectRef.current`,
 * or an explicit `options.force` for callers that navigate instead of loading
 * and so have no completeProjectLoad tail to retry). Both exemptions are strict
 * wideners. The six remaining body reads of the closure `currentPage` were
 * deliberately left alone — two are additionally gated on isLoadingProjectRef
 * and one is unreachable for a quotemaker target, so the exposure is not live
 * and changing them is a separate blast radius.
 */

import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { addressNamesPage, isPassiveMonitoringExcursion, pageToPath } from '../utils/page-routing';
import { historyViewState, resolveHistoryQuoteStageTarget, resolveHistorySummaryTarget } from '../utils/project-stage';
import { isQuoteRunActiveError, isQuoteRunJobShown } from '../services/quote-run-open-guard';
import { applyExtractedRequesters, seedSiteContactsFromIntake } from '../services/rfq-requesters';
import {
    isBackendReachable,
    isServerUnavailableError,
    subscribeBackendReachability,
} from '../services/network-resilience';

/**
 * Where the operator was, and what had painted, when a reopen of the tab's
 * saved project started. A reopen held through a server outage keeps the
 * first attempt's, so "moved on" is measured from that attempt.
 */
interface SavedProjectResumeOrigin {
    startedOnPage: string;
    paintedAtStart: unknown;
}
type ResumeSavedProject = (projectId: string, heldFrom?: SavedProjectResumeOrigin) => void;

// A reopen that met a server outage waits for the server to answer (review
// 7, B1, 2026-10-04). One waits at a time: a newer reopen replaces it.
let stopSavedProjectWaitingForServer: (() => void) | null = null;
// Each App's latest resumeSavedProject, keyed by its currentPageRef, so a
// reopen that waited minutes runs on the latest render's load chain, not the
// one the outage met (CLAUDE.md: a held callback must never pin the routing
// or load chain). A render-phase assignment, the ref-wrapper idiom without a
// hook, because tests call useAppRouting outside React.
const latestResumeSavedProject = new WeakMap<object, ResumeSavedProject>();

/** Minimal structural view of App's formData — only fields this seam touches. */
interface RoutingFormData {
    address?: string;
    notes?: string;
    clientName?: string;
    clientCompany?: string;
    jobDescription?: string;
    [key: string]: unknown;
}

/** Return shape of editor.syncBack — only `.scope` is read here. */
interface SyncedQuoteLike {
    header?: {
        title?: unknown;
        [key: string]: unknown;
    };
    scope?: unknown;
    [key: string]: unknown;
}

/** Validated sessionStorage snapshot returned by App's readQmStateFromStorage. */
interface QmStoredViewState {
    stage?: number;
    summaryViewActive?: boolean;
    quoteEditorOpen?: boolean;
    importPhase?: string;
    savedAt?: number;
    [key: string]: unknown;
}

/**
 * An open whose project has not painted yet (App's pendingProjectOpenRef):
 * an "Open in QuoteMaker" or a plain open. `paintedBefore` is the painted
 * load when it started.
 */
export interface PendingProjectOpen {
    projectId: string;
    paintedBefore: unknown;
}

interface GlobalFileExplorerState {
    open: boolean;
    [key: string]: unknown;
}

/** The DEC-COUNCIL-013 useQuoteEditor surface — only the two methods this seam calls. */
interface QuoteEditorLike {
    reset: () => void;
    suspendForPageNavigation?: () => void;
    restoreFromSession: (opts: { open: boolean }) => unknown;
    /** The controller's live open flag (a ref, current without a render). Headless callers may omit it. */
    openRef?: { current?: unknown };
    /** The controller's close. `transitionToStage4: false` leaves the destination stage to the caller. */
    close?: (options?: { transitionToStage4?: boolean }) => unknown;
    [key: string]: unknown;
}

interface ToastPayload {
    message: string;
    type: string;
    /** Stays until dismissed (App's toast auto-clear skips it). */
    sticky?: boolean;
}

/** Options bag accepted by handleNavigate (annotation added at the move). */
export interface HandleNavigateOptions {
    /** Intake reverse-link: job to auto-select after landing on /intake. */
    selectJobId?: unknown;
    /** CRM "New Quote" prefill for a fresh quotemaker project. */
    prefill?: Record<string, unknown>;
    /** Intake "Open in QuoteMaker" bridge payload. */
    intakeProjectLoad?: {
        projectId: string;
        intakeJobId?: unknown;
        jobSnapshot?: Record<string, unknown> | null;
        /**
         * Called once the project has painted (never when the open fails).
         * The Assistant moves to the stage it named from here.
         */
        onLanded?: () => void;
    };
    /** Cross-page navigation options consumed once by the target page. */
    pageNavOptions?: unknown;
    /**
     * Routing-only: this navigation MUST land the operator, so exempt it from
     * the mid-transition block. For callers that navigate INSTEAD of loading a
     * project and therefore have no completeProjectLoad tail to retry — a
     * swallowed call is permanent for that click. Never bypasses the same-page
     * no-op, and it is stripped before that test so a force-only bag cannot
     * make a same-page call replay the page transition.
     */
    force?: boolean;
    /**
     * Routing-only: called once this navigation is ACCEPTED, i.e. past the
     * page-exit guard and every early return. A navigation the guard holds keeps
     * this callback in its continuation and runs it when the operator confirms
     * the exit; one the operator refuses never runs it. For a caller that has a
     * follow-up to apply to the page it is going to (the result ribbon's stage)
     * and must not apply it to a navigation that did not happen. Stripped before
     * the same-page test, like `force`.
     */
    onAccepted?: () => void;
    /** Internal continuation marker after the host approves an unsaved-work exit. */
    skipPageExitGuard?: boolean;
    /**
     * Routing-only, set by handleNavigate on that continuation: the held
     * navigation was a project load's landing (isLoadingProjectRef was set
     * when it was made). The host runs the continuation later, after the load
     * has painted and cleared that guard, so the continuation carries it: no
     * no-project fast path, no saved-view restore, exempt from the
     * mid-transition block. Stripped before the same-page test.
     */
    projectLoadOwnsLanding?: boolean;
    [key: string]: unknown;
}

export interface PageExitRequest {
    fromPage: string;
    toPage: string;
    source: 'navigation' | 'history';
    /** Continue the exact navigation after the host has obtained approval. */
    proceed: () => void;
    /** Restore the current history entry when a browser-history exit is kept. */
    stay: () => void;
}

/** Mirror of the T5 slice-5 options bag (the popstate handler passes transitionToStage5 only). */
interface OpenStage5FromCurrentScopeOptions {
    sourceQuoteData?: unknown;
    transitionToStage5?: boolean;
    persistStage?: boolean;
}

export interface UseAppRoutingDeps {
    // ── State values (fresh each render, same as the original closures) ──
    currentPage: string;
    stage: number;
    isPageTransitioning: boolean;
    receivablesDisabled: boolean;
    globalFileExplorer: GlobalFileExplorerState;
    ribbonNotification: unknown;
    quoteEditorOpen: boolean;
    formData: RoutingFormData;
    photos: unknown[];
    documents: unknown[];
    videos: unknown[];
    scope: unknown[];
    quoteData: unknown;
    quoteTitle: string;
    titleEditedByUser: boolean;
    /** Live mirrors read after syncBack's synchronous authority publication. */
    getLiveQuoteTitle: () => unknown;
    getLiveTitleEditedByUser: () => unknown;
    analysis: unknown;
    currentProjectId: string | null;
    // ── App-owned routing constants (test-pinned in App.jsx) ──
    PAGE_ORDER: string[];
    QM_STATE_KEY: string | null;
    // ── React state setters (App-owned) — setToast/setImportPhase typed as
    // plain callables per the T5 convention. ──
    setIntakePendingSelectJobId: Dispatch<SetStateAction<unknown>>;
    setGlobalFileExplorer: Dispatch<SetStateAction<GlobalFileExplorerState>>;
    setRibbonNotification: Dispatch<SetStateAction<unknown>>;
    setRibbonDismissing: Dispatch<SetStateAction<boolean>>;
    setCurrentProjectId: Dispatch<SetStateAction<string | null>>;
    setStage: Dispatch<SetStateAction<number>>;
    // Write view is Record<string, unknown>: the intake seed writes
    // prompt-shaped unknowns; RoutingFormData above types only the reads.
    setFormData: Dispatch<SetStateAction<Record<string, unknown>>>;
    setToast: (toast: ToastPayload) => void;
    setImportPhase: (phase: string) => void;
    setCurrentPage: Dispatch<SetStateAction<string>>;
    setIsPageTransitioning: Dispatch<SetStateAction<boolean>>;
    setStageDirection: Dispatch<SetStateAction<string>>;
    /**
     * One-shot request for a Stage 1 Job cockpit tab. A history entry or saved
     * view written while the old Project Summary page was open restores Stage 1
     * with the Summary tab. Optional so a headless caller ignores it.
     */
    requestCockpitTab?: (tab: 'summary' | 'files') => void;
    setPageNavOptions: Dispatch<SetStateAction<unknown>>;
    setPageDirection: Dispatch<SetStateAction<string>>;
    setPrevPage: Dispatch<SetStateAction<string | null>>;
    setIsStageTransitioning: Dispatch<SetStateAction<boolean>>;
    setPrevStage: Dispatch<SetStateAction<number>>;
    // ── Live refs (App-owned) ──
    isLoadingProjectRef: MutableRefObject<boolean>;
    /**
     * Live mirror of `currentPage` (App.jsx:930). handleNavigate is a plain
     * per-render closure, so a memoised caller can pin a stale `currentPage`
     * and defeat the same-page no-op guard. The guard reads this instead.
     */
    currentPageRef: MutableRefObject<string>;
    isPopstateNav: MutableRefObject<boolean>;
    canNavigateToQuoteStageRef: MutableRefObject<(candidate: number) => unknown>;
    /**
     * True while a Project Summary exists or is being produced. A history entry that names the Summary asks for the Summary
     * tab only when this is true (see resolveHistorySummaryTarget). A live probe, not a value: the listener outlives renders.
     * Optional so a headless caller sees a Summary as available.
     */
    canShowSummaryView?: () => unknown;
    // ── App-owned helpers (declared above the instantiation) ──
    readQmStateFromStorage: () => QmStoredViewState | null;
    /**
     * The project load that last painted (App's paintedProjectLoadContextRef).
     * completeProjectLoad replaces it synchronously, so a new value naming the
     * requested id proves an "Open in QuoteMaker" landed. Without it the
     * bridge seeds after any load that resolves, as before 2026-09-29.
     * The value read while a render builds these closures is kept, so a
     * closure run later can tell a project has painted since (2026-09-30).
     */
    readPaintedProjectLoad?: () => { projectId?: unknown; loadGeneration?: unknown } | null | undefined;
    /**
     * The open still loading, or null (App-owned ref): the intake bridge
     * below sets it, and so does the persistence hook's loadProjectUnchecked
     * for every load that lands QuoteMaker (a Run Sheet row, the Assistant).
     * While it is set and nothing has painted since, a plain navigation to
     * QuoteMaker (Menu, Back, the Assistant) waits for it instead of painting
     * an empty QuoteMaker. Needs readPaintedProjectLoad.
     */
    pendingProjectOpenRef?: MutableRefObject<PendingProjectOpen | null>;
    /** Withdraw an automatic Summary's right to select its tab before route state moves (attended-summary-run.ts). */
    revokeAttendedAutomaticRiskIntent: () => void;
    /**
     * App's navigation-intent epoch (the same ref useStageNavigation and the
     * re-quote runner read). It advances the moment a navigation is ACCEPTED
     * (past the page-exit guard and the early returns), not when the deferred
     * `setCurrentPage` finally renders 50ms later. A pending Stage 3 Next or
     * re-quote extraction that settles inside that window therefore still sees
     * that the operator has chosen somewhere else. Optional so headless callers
     * are unchanged.
     */
    navigationIntentEpochRef?: MutableRefObject<number>;
    /**
     * App's accepted-OPERATOR-navigation receipt (packet 1B-B). Advanced at the
     * same acceptance point as the dep above. Unlike that epoch it is never
     * advanced by the pipeline's own stage changes, so a Risk or Work Method run
     * can ask at its delayed handoff whether the operator navigated since it
     * began presenting. Optional so headless callers are unchanged.
     */
    operatorNavigationEpochRef?: MutableRefObject<number>;
    saveCurrentProject: (overrides?: Record<string, unknown>) => Promise<unknown>;
    /** Compare the exact leave-navigation envelope against the last canonical receipt. */
    shouldSaveProjectBeforeNavigation?: (overrides?: Record<string, unknown>) => boolean;
    isQuoteGenerationInFlight: () => boolean;
    /** Optional host-owned unsaved-work gate. Return true when navigation is deferred. */
    requestPageExit?: (request: PageExitRequest) => boolean;
    editor: QuoteEditorLike;
    // ── Late-declared App deps (TDZ) — lazy wrappers, see header note ──
    /**
     * The persistence hook's loadProject. `shouldPaint` is asked once, just
     * before the project would paint; false leaves the screen as it is. The
     * reopen of the tab's saved project passes it (2026-09-29).
     */
    loadProject: (projectSummary: { id: string }, options?: { shouldPaint?: () => boolean }) => Promise<unknown>;
    resetForm: () => void;
    syncQuoteEditorBackToState: () => SyncedQuoteLike | null;
    openStage5FromCurrentScope: (opts?: OpenStage5FromCurrentScopeOptions) => unknown;
    /**
     * Decision D1 (2026-09-28): true when a Quote Run refuses this project
     * change. The refusal has already shown its one message. The persistence
     * hook's refusal, threaded as a lazy wrapper like loadProject.
     */
    refuseProjectChangeDuringQuoteRun?: (change: 'open' | 'reset', projectId?: string) => boolean;
    /**
     * A3 review: true when the project is the job the Quote Run is driving.
     * It has been shown (QuoteMaker, no load), so the caller must not load or
     * seed it. Threaded as a lazy wrapper like loadProject.
     */
    showQuoteRunJobInsteadOfOpening?: (projectId?: string) => boolean;
    /**
     * A3 review round 3: the job the active Quote Run is driving, or null.
     * The fast path counts it as the project in progress even before its load
     * has painted. Threaded as a lazy wrapper like loadProject.
     */
    quoteRunJobInProgress?: () => string | null;
    /**
     * Outage lane review (2026-09-29): paint back a new project that was never
     * saved and was kept on this device while QuoteMaker's server was not
     * answering. Resolves true when it painted. Lazy wrapper like loadProject.
     */
    restoreKeptNewProject?: () => Promise<boolean> | boolean;
}

export function useAppRouting(deps: UseAppRoutingDeps) {
    const {
        currentPage,
        stage,
        isPageTransitioning,
        receivablesDisabled,
        globalFileExplorer,
        ribbonNotification,
        quoteEditorOpen,
        formData,
        photos,
        documents,
        videos,
        scope,
        quoteData,
        quoteTitle,
        titleEditedByUser,
        getLiveQuoteTitle,
        getLiveTitleEditedByUser,
        analysis,
        currentProjectId,
        PAGE_ORDER,
        QM_STATE_KEY,
        setIntakePendingSelectJobId,
        setGlobalFileExplorer,
        setRibbonNotification,
        setRibbonDismissing,
        setCurrentProjectId,
        setStage,
        setFormData,
        setToast,
        setImportPhase,
        setCurrentPage,
        setIsPageTransitioning,
        setStageDirection,
        requestCockpitTab,
        setPageNavOptions,
        setPageDirection,
        setPrevPage,
        setIsStageTransitioning,
        setPrevStage,
        isLoadingProjectRef,
        currentPageRef,
        isPopstateNav,
        canNavigateToQuoteStageRef,
        canShowSummaryView,
        readQmStateFromStorage,
        readPaintedProjectLoad,
        pendingProjectOpenRef,
        revokeAttendedAutomaticRiskIntent,
        navigationIntentEpochRef,
        operatorNavigationEpochRef,
        saveCurrentProject,
        shouldSaveProjectBeforeNavigation,
        isQuoteGenerationInFlight,
        requestPageExit,
        editor,
        loadProject,
        resetForm,
        syncQuoteEditorBackToState,
        openStage5FromCurrentScope,
        refuseProjectChangeDuringQuoteRun,
        showQuoteRunJobInsteadOfOpening,
        quoteRunJobInProgress,
        restoreKeptNewProject,
    } = deps;

    // The project load that had painted when this render made these closures
    // (review 2026-09-30, round 4). A caller can run a closure made renders
    // ago: a landing the page-exit question held, a timer, a caller that
    // awaited. Once a project has painted since, that closure's "nothing is
    // open" is stale, and the load guard no longer says so (the load
    // released it). The fast path then reset the job to an empty new project
    // or reopened the tab's older job over it, and the restore replayed the
    // older job's view on it. Those three never act from such a closure: the
    // project on screen is shown as it is. A reset is not a paint: a closure
    // made before one still opens a new project. Nor is a new project's first
    // save: App restamps the painted load with the id the save gave it, in
    // the same load generation, and the project on screen is the same one.
    const paintedWhenMade = readPaintedProjectLoad?.() ?? null;
    const projectPaintedSinceMade = (): boolean => {
        if (!readPaintedProjectLoad) return false;
        const painted = readPaintedProjectLoad() ?? null;
        if (painted?.projectId == null || painted.projectId === '') return false;
        // Every project load claims a new generation, so a paint moves it.
        if (painted.loadGeneration !== paintedWhenMade?.loadGeneration) return true;
        // Same generation: another project on screen still counts.
        const madeId = paintedWhenMade?.projectId;
        return madeId != null && madeId !== '' && String(madeId) !== String(painted.projectId);
    };

    // "No active project": no loaded project, no scope, no analysis, no
    // quote, no photos/docs/videos, and nothing in the intake form. In that
    // state QuoteMaker has nothing on screen to return to.
    // Advances App's navigation-intent epoch (see the dep's note). Only called
    // once a navigation is accepted, so it cannot make a no-op or a held exit
    // cancel a normal Next.
    //
    // A passive monitoring trip (Developer Tools, AI Models) still advances the
    // navigation-intent epoch, so a pending Stage 3 Next or re-quote extraction
    // does not move a page the operator has stepped away from. It does NOT advance
    // the accepted-OPERATOR-navigation receipt: that receipt answers "did the operator
    // choose somewhere else than this run", and watching the platform is not that
    // choice (RFQ 903025: a Risk or Work Method run stopped presenting because the
    // operator opened the Live Monitor to watch it).
    const markNavigationAccepted = (passiveMonitoringTrip = false): void => {
        if (navigationIntentEpochRef) navigationIntentEpochRef.current += 1;
        if (operatorNavigationEpochRef && !passiveMonitoringTrip) operatorNavigationEpochRef.current += 1;
    };
    const quoteMakerHasActiveWork = () => !!currentProjectId
        || !!analysis
        || !!quoteData
        || scope.length > 0
        || photos.length > 0
        || documents.length > 0
        || videos.length > 0
        || !!formData.notes?.trim()
        || !!formData.address?.trim()
        || !!formData.clientName?.trim();

    // The project this tab last had open in QuoteMaker, from its saved record
    // (sessionStorage qm_last_view_state::<tenant>), or ''.
    const readResumableProjectId = (): string => {
        const storedForResume = readQmStateFromStorage();
        return typeof storedForResume?.currentProjectId === 'string'
            ? storedForResume.currentProjectId.trim()
            : '';
    };

    // The open whose project has not painted yet, or null: an "Open in
    // QuoteMaker" (the bridge below) or a plain open (loadProjectUnchecked
    // records it, 2026-09-30). completeProjectLoad replaces the painted load
    // before its own handleNavigate('quotemaker'), so once anything has
    // painted this is null and that landing (or a newer open's) goes through
    // (2026-09-29).
    const openStillLoading = (): PendingProjectOpen | null => {
        const pending = pendingProjectOpenRef?.current ?? null;
        if (!pending || !readPaintedProjectLoad) return null;
        return readPaintedProjectLoad() === pending.paintedBefore ? pending : null;
    };

    // Menu > QuoteMaker, Back or the Assistant while that open loads: nothing
    // is painted (QuoteMaker would be empty, and the view-state effect would
    // save that over the tab's record). The open lands the page when it
    // paints, or says why it could not.
    const sayOpenStillLoading = (pending: PendingProjectOpen) => {
        setToast({
            message: `Still opening project ${String(pending.projectId).slice(0, 8)}. QuoteMaker shows it as soon as it has loaded.`,
            type: 'info',
        });
    };

    // Reopen the tab's saved project. The load lands the page:
    // completeProjectLoad calls handleNavigate('quotemaker') as its last
    // statement. Shared by the no-active-work fast path (a reload, Menu >
    // QuoteMaker, the Assistant) and browser Back into QuoteMaker.
    //
    // 2026-09-29 (Customer Zero: "randomly resets ... back to quotemaker page
    // (empty new project page)"): a failed resume no longer deletes the record
    // and shows an empty project, and nothing it does moves the operator off a
    // page they chose. After a reload the Run Sheet is on screen for the 1 to
    // 5 seconds the job takes to load, so the operator is told it is
    // reopening, and the job paints (and lands QuoteMaker) only while they are
    // still on the page the resume started from and nothing else has painted.
    // Otherwise it is left unpainted: the record still names it, and Menu >
    // QuoteMaker reopens it.
    //
    // `heldFrom` is a reopen that met a server outage and waited for the
    // server to answer (review 7, B1). It runs only while nothing has
    // happened since its first attempt: the operator is on the same page,
    // nothing has painted or been reset (a new project replaces the painted
    // load too), the record still names the project, no other open is
    // loading, no Quote Run has a job, and QuoteMaker holds no work.
    const resumeSavedProject = (projectId: string, heldFrom?: SavedProjectResumeOrigin): void => {
        // A reopen waiting for the server is replaced by this one.
        stopSavedProjectWaitingForServer?.();
        // Where the operator was, and what had painted, when the resume
        // started.
        const startedOnPage = heldFrom ? heldFrom.startedOnPage : (currentPageRef?.current || currentPage);
        const paintedAtStart = heldFrom ? heldFrom.paintedAtStart : readPaintedProjectLoad?.();
        const operatorMovedOn = () => (currentPageRef?.current || currentPage) !== startedOnPage
            || (!!readPaintedProjectLoad && readPaintedProjectLoad() !== paintedAtStart);
        if (heldFrom && (operatorMovedOn()
            || readResumableProjectId() !== projectId
            || isLoadingProjectRef.current
            || !!openStillLoading()
            || !!quoteRunJobInProgress?.()
            || quoteMakerHasActiveWork())) {
            console.info('[QM] QuoteMaker\'s server answers again, but the operator has moved on, so the saved project is not reopened by itself. Menu > QuoteMaker reopens it.');
            return;
        }
        // Before the load starts: a Quote Run's refusal is shown inside
        // loadProject and must be the message left on screen.
        setToast({
            message: 'Reopening the project this tab last had open. QuoteMaker shows it when it has loaded, if you stay on this page.',
            type: 'info',
        });
        const shouldPaint = () => {
            if (!operatorMovedOn()) return true;
            setToast({
                message: 'The project this tab last had open was not reopened, because you moved on before it loaded. Open QuoteMaker to reopen it.',
                type: 'info',
            });
            return false;
        };
        Promise.resolve(loadProject({ id: projectId }, { shouldPaint })).catch((err) => {
            // A Quote Run refused the resume and has shown its message (D1).
            // The saved project is fine: keep the receipt, reset nothing and
            // stay on this page.
            if (isQuoteRunActiveError(err)) return;
            const status = (err as { status?: number })?.status;
            const reason = (err as { message?: string })?.message || '';
            // Auth failure ≠ project failure (reload-resume race, 2026-08-05).
            // A 401/403 here means the SESSION died (expired token, rotated
            // refresh, backend session wipe). The project itself is fine, so
            // the receipt is kept for LoginPage's post-login hard reload, which
            // re-runs this resume with fresh auth. apiFetch has already fired
            // qm:session-expired, whose sticky "please log in again" toast owns
            // the messaging. Nothing is painted: an empty QuoteMaker painted
            // while the tenant key was still set could be saved over the
            // receipt, and the sign-in page takes over anyway.
            //
            // A restore that met a server outage (outage lane, 2026-09-29) is
            // not a failed project either: keep the receipt, and nothing is
            // painted for the same reason as above.
            //
            // Review 7, B1 (2026-10-04): the "Reopening..." notice was left on
            // screen after this load had failed, and nothing reopened the job
            // when the server answered. While the server is marked as not
            // answering (the read proxy and apiFetch mark it, and the
            // reconnect loop clears it), the reopen waits and runs once it
            // answers, under the guards above, and the notice says so. If it
            // is not marked, nothing would retry, so the notice names the way
            // to retry instead.
            const serverAway = isServerUnavailableError(err);
            if (serverAway) {
                console.warn("[QM] Session-resume paused: QuoteMaker's server is not answering; keeping the saved project receipt.");
                if (!isBackendReachable()) {
                    reopenWhenServerAnswers(projectId, { startedOnPage, paintedAtStart });
                    setToast({
                        message: "The project this tab last had open reopens by itself when QuoteMaker's server answers, if you stay on this page.",
                        type: 'info',
                    });
                } else {
                    setToast({
                        message: "QuoteMaker's server did not answer, so the project this tab last had open was not reopened. Nothing is lost. Open Menu > QuoteMaker to try again.",
                        type: 'warning',
                        sticky: true,
                    });
                }
                return;
            }
            if (status === 401 || status === 403) {
                console.warn('[QM] Session-resume paused — not signed in; keeping the saved project receipt.');
                return;
            }
            // The project no longer exists (v6.0.19: a stale receipt, not a
            // failure). Forget it and say so; change nothing else.
            //
            // Review round 4 (2026-09-29): this used to open a new project.
            // The answer arrives seconds after a reload, while the operator
            // reads the Run Sheet or has pressed Open on another job, so the
            // new project pulled them off the Run Sheet onto an empty
            // QuoteMaker, and resetForm (which claims a new load generation)
            // threw away the job they were opening. The message stays until
            // dismissed; the next Menu > QuoteMaker opens a new project
            // because the operator asks for one.
            if (status === 404 || /not found/i.test(reason)) {
                // Forget the record only while it still names this project:
                // a project opened since has its own.
                if (readResumableProjectId() === projectId) {
                    try { if (QM_STATE_KEY) sessionStorage.removeItem(QM_STATE_KEY); } catch {}
                }
                // A reload lands the Run Sheet with the address still naming
                // /quotemaker (the mount seed keeps it), and Back into
                // QuoteMaker moved it there. With nothing left to reopen, the
                // next reload (the next deploy) would land an empty QuoteMaker
                // from that address, so it goes back to the page on screen.
                const livePage = currentPageRef?.current || currentPage;
                if (!addressNamesPage(window.location.pathname, livePage)) {
                    const entry = window.history.state?.page === livePage ? window.history.state : { page: livePage, stage };
                    const entryPath = pageToPath(livePage) + (window.location.search || '') + (window.location.hash || '');
                    try { window.history.replaceState(entry, '', entryPath); } catch { /* address cosmetics only */ }
                }
                setToast({
                    message: 'The project this tab last had open no longer exists, so this tab has forgotten it. Nothing else was changed.',
                    type: 'warning',
                    sticky: true,
                });
                return;
            }
            // Anything else (a 500, a refused request, a load that threw): the
            // project is still saved. Keep the receipt, reset nothing, stay on
            // this page and say so. Opening QuoteMaker again retries, and so
            // does a reload, because the address still names QuoteMaker. (A
            // server outage is answered above.)
            console.warn('[QM] Could not reopen the saved QuoteMaker project; keeping its receipt:', reason || err);
            setToast({
                message: `Could not reopen the project you had open: ${reason || 'unknown error'}. Nothing is lost. Open QuoteMaker again to retry.`,
                type: 'error',
                sticky: true,
            });
        });
    };
    if (currentPageRef) latestResumeSavedProject.set(currentPageRef, resumeSavedProject);

    // Hold a reopen that met a server outage until the server answers: the
    // reconnect loop's healthy probe, or any 2xx, flips the reachability
    // signal. Then the latest render's resumeSavedProject runs it once, from
    // the first attempt's origin, after the listeners have all heard.
    const reopenWhenServerAnswers = (projectId: string, heldFrom: SavedProjectResumeOrigin) => {
        stopSavedProjectWaitingForServer?.();
        let unsubscribe: (() => void) | null = null;
        const stop = () => {
            unsubscribe?.();
            unsubscribe = null;
            if (stopSavedProjectWaitingForServer === stop) stopSavedProjectWaitingForServer = null;
        };
        unsubscribe = subscribeBackendReachability((reachable) => {
            if (!reachable || !unsubscribe) return;
            stop();
            const resume = (currentPageRef && latestResumeSavedProject.get(currentPageRef)) || resumeSavedProject;
            void Promise.resolve().then(() => resume(projectId, heldFrom));
        });
        stopSavedProjectWaitingForServer = stop;
    };

    // Is the Stage 5 editor open RIGHT NOW? App's popstate listener is registered again only when currentPage, stage or the
    // tenant-session flag changes, so a handler it holds can be several renders old: the `quoteEditorOpen` it closed over may
    // be stale (the editor opens and closes without moving any of those). The controller's own ref is live. A headless caller
    // without one falls back to the value this render was made with.
    const editorIsOpenNow = (): boolean => {
        const openRef = editor?.openRef;
        return typeof openRef?.current === 'boolean' ? openRef.current === true : quoteEditorOpen;
    };

    // Leaving QuoteMaker for another page, by the menu or by browser history (C16): capture what the open editor shows, save it
    // once if it is a meaningful change, and suspend the editor so no quote overlay is left above the page that opens. Moved out
    // of handleNavigate (which still calls it at the same point; the only change is that the open state is read live) so a
    // history departure follows the same lifecycle. It is passive: it never runs the explicit Save's review and learning workflow.
    const leaveQuoteMakerForAnotherPage = (): void => {
        const quoteGenerationOwnsProject = isQuoteGenerationInFlight();
        const syncedQuote = editorIsOpenNow() && !quoteGenerationOwnsProject
            ? syncQuoteEditorBackToState()
            : null;
        const worthSaving = formData.address?.trim() || formData.notes?.trim() ||
            formData.clientName?.trim() || formData.clientCompany?.trim() ||
            formData.jobDescription?.trim() || photos.length > 0 || documents.length > 0 ||
            videos.length > 0 || scope.length > 0 || !!syncedQuote || !!quoteData || !!analysis;
        const liveQuoteTitle = String(getLiveQuoteTitle?.() ?? quoteTitle ?? '').trim();
        const liveTitleEditedByUser = getLiveTitleEditedByUser?.() === true;
        const navigationSaveOverrides = syncedQuote ? {
            quoteData: syncedQuote,
            scope: syncedQuote.scope,
            // syncedQuote exists only when quoteEditorOpen, and an
            // open editor IS Stage 5 (virtual stage) — persist the
            // literal, matching the App.jsx save-quote sibling.
            currentStage: 5,
            ...(liveQuoteTitle ? {
                // syncBack publishes confirmed title authority to the live
                // refs synchronously. Read those refs after sync rather than
                // inferring user intent from a legacy header/title mismatch.
                quoteTitle: liveQuoteTitle,
                titleEditedByUser: liveTitleEditedByUser,
            } : {}),
        } : undefined;
        const hasMeaningfulChanges = typeof shouldSaveProjectBeforeNavigation === 'function'
            ? shouldSaveProjectBeforeNavigation(navigationSaveOverrides)
            : true;
        if (worthSaving && hasMeaningfulChanges && !quoteGenerationOwnsProject) {
            // Fire-and-forget: the save impl toasts + banners on failure
            // and re-throws, so the catch only keeps a failed autosave
            // from surfacing as an unhandled rejection mid-navigation.
            saveCurrentProject(navigationSaveOverrides).catch((err) => {
                console.warn('[QM] nav-away autosave failed (continuing):', (err as { message?: string })?.message || err);
            });
        }
        if (editorIsOpenNow()) {
            if (typeof editor.suspendForPageNavigation === 'function') {
                editor.suspendForPageNavigation();
            } else {
                editor.reset();
            }
        }
    };

    const handleNavigate = (newPage: string, options?: HandleNavigateOptions) => {
        // Project History retired as a destination (operator, 2026-07-28): the
        // Run Sheet IS the project list. Every legacy 'quotes' navigation (row
        // fallbacks, post-delete paths, /quotes deep links, Assistant tool
        // calls) lands on the dashboard instead of a menu-less orphan page.
        // The AllQuotesPage code stays in the tree, dormant, like
        // dashboard-v4.css.
        const requested = typeof newPage === 'string' ? newPage : newPage;
        const page = requested === 'quotes' ? 'dashboard' : requested;
        // Same-page navigation is a no-op ONLY when it carries no options.
        // An option-bearing same-page call must run so its payload routes:
        // the Assistant `navigate` tool (App.jsx), the Stage 1 cards' View in
        // Clients deep-links and IntakeContextChip's selectJobId can all target the
        // page the user is already on. Mid-transition, all navigation blocks.
        //
        // ── v5.47.37 — the Run Sheet open-swallow fix ──────────────────────
        // completeProjectLoad() calls handleNavigate('quotemaker') as its LAST
        // statement, after every setState that hydrates the project. So a call
        // swallowed here leaves the project fully loaded and the operator
        // standing on the page they came from: "clicking a row in the Run Sheet
        // does nothing, but Menu -> QuoteMaker shows the quote is open"
        // (operator report 2026-07-30). Worse, the loaded project then drifts
        // from the row the operator believed they opened, so the next
        // bottom-nav title edit renames a DIFFERENT quote. Two legs did it:
        //
        //   1. `currentPage` is a closure value. A memoised caller pins it —
        //      openQuoteForProject (App.jsx) had a dep list omitting
        //      loadProject, so it pinned a chain whose currentPage was stuck at
        //      'quotemaker' and every subsequent row click hit the no-op.
        //      Compare against the live ref; it mirrors the same state.
        //   2. `isPageTransitioning` blocks ALL navigation for 450ms and a fast
        //      row click can resolve inside that window. A project load must
        //      always land the operator, so it is exempt. This is the same
        //      exemption the two blocks below already use, and it can only
        //      permit navigation that was previously dropped — never block
        //      anything new. isLoadingProjectRef is true for the whole of
        //      completeProjectLoad and cleared in a rAF once it commits.
        //   3. A caller that navigates INSTEAD of loading has no
        //      completeProjectLoad tail to fall back on, so a swallowed call is
        //      permanent for that click — and worse than a no-op: the
        //      same-project branch of openQuoteForProject (App.jsx) runs
        //      setStage(5) + editor.peek() regardless, so a swallow leaves the
        //      documented blank pair (stage 5 with the modal open on a page that
        //      cannot mount it). Those callers pass `force` and are exempt too.
        //
        // Both exemptions are strict wideners: they can only permit a
        // navigation that was previously dropped, never block a new one.
        const livePage = currentPageRef?.current || currentPage;
        // A project load owns this landing: completeProjectLoad's closing call
        // (the guard is set), or the continuation of one the page-exit host
        // held (projectLoadOwnsLanding, see requestPageExit below).
        const loadOwnsLanding = isLoadingProjectRef.current === true || options?.projectLoadOwnsLanding === true;
        const mustLand = loadOwnsLanding || options?.force === true;
        // Routing-only flags are stripped before the same-page test: a bag
        // carrying nothing else must not turn a same-page call into a full body
        // run, which would replay the page-enter animation and re-arm the 450ms
        // lockout for nothing.
        const payloadOptions = options && Object.keys(options)
            .some((key) => key !== 'force' && key !== 'skipPageExitGuard' && key !== 'projectLoadOwnsLanding' && key !== 'onAccepted')
            ? options
            : undefined;
        if (page === livePage && !payloadOptions) return;
        if (isPageTransitioning && !mustLand) return;
        if (page === 'receivables' && receivablesDisabled) return;
        // Decision D1 (2026-09-28): "Open in QuoteMaker" (Intake, a Clients
        // dossier) during a Quote Run would abort the running job. Refuse
        // before anything moves, so the operator stays on the page they
        // clicked from. The refusal has already shown the one Quote Run message.
        // The job the run is driving is shown instead (A3 review): no load,
        // and no seeding of its form from the intake snapshot.
        if (page === 'quotemaker' && options?.intakeProjectLoad) {
            const intakeProjectId = options.intakeProjectLoad.projectId;
            if (showQuoteRunJobInsteadOfOpening?.(intakeProjectId) === true) return;
            if (refuseProjectChangeDuringQuoteRun?.('open', intakeProjectId) === true) return;
        }
        // An "Open in QuoteMaker" is still loading (2026-09-29). The bridge
        // holds isLoadingProjectRef for the whole fetch, so the fast path and
        // the restore below are skipped and this call would paint QuoteMaker
        // with whatever is in state: after a reload, an empty new project.
        // Wait for the open instead. A new open or a CRM prefill is its own
        // request and goes through. So does a must-land (force) call: it
        // navigates INSTEAD of loading, shows the job already on screen (or
        // the one it just set up) and has no load to land it later, so a
        // dropped call is permanent (CLAUDE.md routing rule). Dropped, Open
        // quote on the open job left its editor and stage 5 on a page that
        // cannot show them.
        if (page === 'quotemaker' && !options?.intakeProjectLoad && !options?.prefill && options?.force !== true) {
            const pending = openStillLoading();
            if (pending) {
                sayOpenStillLoading(pending);
                return;
            }
        }
        // A held project load landing (review 2026-09-30). completeProjectLoad
        // lands the page with the handleNavigate of the render that STARTED
        // the load, and so does this continuation. Settings with unsaved
        // edits runs it on "Discard changes", after the load has painted and
        // cleared isLoadingProjectRef. That render saw nothing loaded, so the
        // continuation took the no-project fast path (an empty new project)
        // or reopened the tab's saved job over the one just opened. It
        // carries the load's ownership of the landing instead.
        if (page !== livePage
            && options?.skipPageExitGuard !== true
            && requestPageExit?.({
                fromPage: livePage,
                toPage: page,
                source: 'navigation',
                proceed: () => handleNavigate(page, {
                    ...(options || {}),
                    skipPageExitGuard: true,
                    ...(loadOwnsLanding ? { projectLoadOwnsLanding: true } : {}),
                }),
                stay: () => {},
            }) === true) return;
        // Navigation is operator intent, not autonomous-pipeline authority.
        // Revoke synchronously before delayed page/view restoration can make a
        // previously valid Summary token appear current again.
        //
        // One exception: a passive monitoring trip (QuoteMaker <-> Developer Tools /
        // AI Models, same project). Watching the platform is not a choice about the
        // run, and revoking here cancelled the Summary -> Risk continuation with no
        // message (RFQ 903025, 2026-10-18). It is NEVER passive when this call
        // loads or creates a project (a load's landing, a prefill, an intake open,
        // a must-land force): those change the owner and revoke as before. STOP,
        // Back, the Data Entry pill, an evidence edit, a review open and every other
        // page still revoke through their own paths.
        const passiveMonitoringTrip = !loadOwnsLanding
            && options?.force !== true
            && !options?.prefill
            && !options?.intakeProjectLoad
            && isPassiveMonitoringExcursion(livePage, page);
        if (!passiveMonitoringTrip) revokeAttendedAutomaticRiskIntent();
        // The same acceptance revokes any pending completion's NAVIGATION
        // authority (a Stage 3 Next still preparing, a re-quote extraction).
        // This is the moment the operator's choice is accepted, past the exit
        // guard and the early returns above, so it holds during the 50ms before
        // the deferred page flip renders. A held exit, a same-page no-op and
        // the transition lockout all returned above and never get here.
        markNavigationAccepted(passiveMonitoringTrip);
        options?.onAccepted?.();

        // v5.36.30: route the intake reverse-link option. Set the pending
        // select state before flipping currentPage so the mount-effect in
        // IntakePage picks it up on first render.
        if (page === 'intake' && options?.selectJobId) {
            setIntakePendingSelectJobId(options.selectJobId);
        }

        // Close FileExplorer on navigation. Otherwise: operator clicks a page
        // from the application menu, the app navigates beneath, but the
        // FileExplorer modal at z-9999 keeps the navigated page hidden behind
        // it. Closing on nav matches operator intent — picking a page means
        // wanting to see that page, not the picker. Selection state is
        // preserved on the explorer object so re-opening resumes where the
        // operator left off. The Quote Run page is the explorer: going to it
        // keeps it open, and App closes it once the page has changed, so it
        // never closes under the page while the next one is still on its way.
        if (globalFileExplorer.open && page !== 'quote-run' && livePage !== 'quote-run') {
            setGlobalFileExplorer(prev => ({ ...prev, open: false }));
        }

        // Auto-dismiss ribbon when navigating to QuoteMaker
        if (page === 'quotemaker' && ribbonNotification) {
            setRibbonNotification(null);
            setRibbonDismissing(false);
        }

        // Auto-save when leaving QuoteMaker if the project has meaningful
        // content. This intentionally does not depend only on hasUnsavedChanges:
        // a fast nav click after editing a contentEditable field can beat the
        // dirty-tracker effect, but the latest formData is already in state.
        if (currentPage === 'quotemaker' && page !== 'quotemaker') leaveQuoteMakerForAnotherPage();

        // Handle prefill data (from CRM "New Quote" button)
        if (options?.prefill && page === 'quotemaker') {
            const pf = options.prefill;
            // Reset to a new project and pre-fill form data
            setCurrentProjectId(null);
            setStage(1);
            setFormData(prev => ({
                ...prev,
                address: pf.address || prev.address || '',
                strataplan: pf.strataplan || prev.strataplan || '',
                suburb: pf.suburb || prev.suburb || '',
                state: pf.state || prev.state || '',
                postcode: pf.postcode || prev.postcode || '',
                clientCompany: pf.clientCompany || prev.clientCompany || '',
                company_id: pf.company_id || null,
                property_id: pf.property_id || null,
            }));
            // Clear stored QM state so we start fresh
            try { if (QM_STATE_KEY) sessionStorage.removeItem(QM_STATE_KEY); } catch {}
        }

        // v5.36.22: intake-side "Open in QuoteMaker" bridge. The operator hits
        // ready-for-quoting in /intake, backend auto-creates a project row
        // (gated by QM_INTAKE_AUTOCREATE_PROJECT_ON_READY), then the Lead
        // Detail's Open-in-QuoteMaker button calls handleNavigate('quotemaker',
        // { intakeProjectLoad: { projectId, intakeJobId, jobSnapshot } }).
        // We fetch the project and call loadProject(), AND seed Stage 1 form
        // fields from the intake job snapshot when the auto-created project
        // is still empty (which it is by default — backend creates with
        // form_data: '{}'). The intake job ID is stashed on formData so
        // Stage 1's folder-import banner can find the source folder.
        //
        // 2026-09-29: the load lands the page, as a Run Sheet row open does.
        // completeProjectLoad calls handleNavigate('quotemaker') as its last
        // statement. This bridge used to move to QuoteMaker 50ms after the
        // click whatever the load did, so a failed open (a 500, an outage)
        // showed an empty new project, or a Stage 1 form seeded from the
        // intake job with no project behind it. Now the operator stays on the
        // page they clicked from until the project paints, and a failed open
        // says so there, in a message that stays. Until it paints, Menu >
        // QuoteMaker and Back wait for it (openStillLoading), and the
        // Assistant's stage move waits for onLanded.
        if (options?.intakeProjectLoad && page === 'quotemaker') {
            const { projectId, intakeJobId, jobSnapshot, onLanded } = options.intakeProjectLoad;
            // The tab's saved-project record is left alone (review round 3,
            // R1). It used to be deleted here and put back only if the open
            // failed in the same page life, so a reload while the project
            // loaded (F5 because nothing seemed to happen, a deploy's
            // service-worker reload) lost it, and the next QuoteMaker was an
            // empty new project saved over the record. The landing does not
            // need it gone: completeProjectLoad holds isLoadingProjectRef, so
            // the restore below is skipped, and the opened project's own view
            // then replaces the record.
            const paintedBeforeOpen = readPaintedProjectLoad?.();
            // Claim loading guard so the loader owns the landing — skips fast-path/restore.
            // completeProjectLoad will release this guard past the next paint.
            isLoadingProjectRef.current = true;
            // Until the project paints, Menu > QuoteMaker, Back and the
            // Assistant wait for it (openStillLoading) instead of painting an
            // empty QuoteMaker. A newer open replaces this record.
            const pendingOpen: PendingProjectOpen = { projectId, paintedBefore: paintedBeforeOpen };
            if (pendingProjectOpenRef) pendingProjectOpenRef.current = pendingOpen;
            const openSettled = () => {
                if (pendingProjectOpenRef?.current === pendingOpen) pendingProjectOpenRef.current = null;
            };
            // Nothing painted: release the guard (completeProjectLoad never
            // ran to do it). The tab's record was never taken, so it still
            // names the job on screen, or whatever the operator asked for
            // since.
            const openDidNotLand = () => {
                openSettled();
                isLoadingProjectRef.current = false;
            };
            (async () => {
                try {
                    // Lightweight summary — loadProject re-fetches the full
                    // record via DataService.projects.getById.
                    const opened = await loadProject({ id: projectId });
                    // The run reached this project after the check above, so
                    // it was shown, not loaded: completeProjectLoad never ran
                    // to release the guard, and the running job's form must
                    // not be seeded.
                    if (isQuoteRunJobShown(opened)) {
                        openSettled();
                        isLoadingProjectRef.current = false;
                        return;
                    }
                    // loadProject can resolve without painting: "Project not
                    // found" (an unreachable backend and no copy on this
                    // device), a newer open took over, or the unsaved-changes
                    // question is still on screen. loadProject or that
                    // question has spoken. Seeding here would write the intake
                    // job into whatever form is on screen.
                    if (readPaintedProjectLoad) {
                        const painted = readPaintedProjectLoad();
                        const landed = !!painted
                            && painted !== paintedBeforeOpen
                            && painted.projectId === projectId;
                        if (!landed) {
                            openDidNotLand();
                            return;
                        }
                    }
                    openSettled();
                    // After load: if the just-loaded project's form_data is
                    // empty (auto-created), seed from the intake snapshot so
                    // Stage 1 isn't a blank form. The check guards against
                    // overwriting fields a user has actually edited.
                    if (jobSnapshot) {
                        setFormData((prev) => {
                            const seedIfEmpty = (k: string, v: unknown) => (prev[k] && String(prev[k]).trim() ? prev[k] : (v || ''));
                            const seeded = {
                                ...prev,
                                clientName: seedIfEmpty('clientName', jobSnapshot.strata_manager_name),
                                clientEmail: seedIfEmpty('clientEmail', jobSnapshot.strata_manager_email),
                                clientPhone: seedIfEmpty('clientPhone', jobSnapshot.strata_manager_phone),
                                clientCompany: seedIfEmpty('clientCompany', jobSnapshot.strata_company_name),
                                strataplan: seedIfEmpty('strataplan', jobSnapshot.strata_plan_no),
                                address: seedIfEmpty('address', [jobSnapshot.property_street, jobSnapshot.property_suburb].filter(Boolean).join(', ')),
                                suburb: seedIfEmpty('suburb', jobSnapshot.property_suburb),
                                jobDescription: seedIfEmpty('jobDescription', jobSnapshot.job_description || jobSnapshot.scope_summary),
                                // Tag this project's form_data with the source
                                // intake job ID + folder so Stage 1 can offer
                                // a folder-import banner.
                                intakeJobId: prev.intakeJobId || intakeJobId || null,
                                intakeFolderName: prev.intakeFolderName || jobSnapshot.folder_name || null,
                                intakeParentBucket: prev.intakeParentBucket || jobSnapshot.parent_bucket || '[1. Quote Requests]',
                            };
                            // Every strata manager on the intake job, primary
                            // first; requesters[0] stays the client fields above.
                            const requesters = applyExtractedRequesters(seeded, jobSnapshot.requesters);
                            const withRequesters = requesters ? { ...seeded, requesters } : seeded;
                            // The site contacts and building manager, kept apart
                            // from the requesters, while the project has none.
                            const site = seedSiteContactsFromIntake(withRequesters as Record<string, unknown>, jobSnapshot);
                            return site ? { ...withRequesters, ...site } : withRequesters;
                        });
                    }
                    // The project is on screen: the Assistant's stage move
                    // may follow. It is the caller's; it cannot unmake the open.
                    try {
                        onLanded?.();
                    } catch (landedErr) {
                        console.warn('[QM] Open in QuoteMaker: the follow-up after the open failed:', (landedErr as { message?: string })?.message || landedErr);
                    }
                } catch (err) {
                    openDidNotLand();
                    // A Quote Run that started after the check above refused
                    // the load and has shown its own message (D1). A second,
                    // different error would contradict it.
                    if (isQuoteRunActiveError(err)) return;
                    console.warn('[QM] intake → Stage 1 bridge failed to load project:', (err as { message?: string })?.message || err);
                    setToast({
                        message: `Could not open project ${projectId.slice(0, 8)}: ${(err as { message?: string })?.message || 'unknown error'}. Nothing was changed, so you can try again.`,
                        type: 'error',
                        sticky: true,
                    });
                }
            })();
            return;
        }

        // ─── Fast-path: clicking QuoteMaker with no active project lands on
        //     the Import Project Files drop zone (same surface as clicking
        //     "New Project" from Dashboard). Peter's 2026-04-19 ask: a fresh
        //     click on the QuoteMaker menu button when nothing is in progress
        //     should ALWAYS take you to import - not restore a stale stage.
        //
        //     "No active project" means: no loaded project, no scope, no
        //     analysis, no quote, no photos/docs/videos, and nothing in the
        //     intake form. In that state there's nothing to restore TO, so
        //     we skip sessionStorage restore + seed importPhase='import'.
        //
        //     CRITICAL: completeProjectLoad() calls handleNavigate('quotemaker')
        //     AFTER setCurrentProjectId + setStage + setFormData etc., but
        //     React batches those state updates. Inside handleNavigate's
        //     closure `currentProjectId` is still null and every *Data.length
        //     is still 0, so the fast-path would misfire and resetForm() the
        //     project we just loaded. Skip the check entirely while
        //     isLoadingProjectRef is set - the loader already knows where
        //     to land the user. A held landing's continuation carries the
        //     same ownership (projectLoadOwnsLanding) and skips it too, and
        //     so does a closure made before a project painted
        //     (projectPaintedSinceMade, review 2026-09-30).
        if (page === 'quotemaker' && currentPage !== 'quotemaker' && !options?.prefill && !options?.intakeProjectLoad && !isLoadingProjectRef.current && options?.projectLoadOwnsLanding !== true
            && !projectPaintedSinceMade()) {
            const hasActiveWork = quoteMakerHasActiveWork();
            if (!hasActiveWork) {
                // A Quote Run is driving a job whose load has not painted yet
                // (A3 review round 3). That job IS the project in progress:
                // reset nothing, reopen no saved project, and replay no stored
                // view (it belongs to another project). Show QuoteMaker; the
                // run's own load lands the job when it paints. Without this,
                // View live asked to stop the run being watched, and a saved
                // record naming the job made the open and the show call each
                // other until the stack ran out. Before the run has a job it
                // is null, and the refusal below still answers (D1).
                const runningJobId = quoteRunJobInProgress?.() ?? null;
                if (runningJobId) {
                    console.info(`[QM] QuoteMaker shows the Quote Run's job ${runningJobId}; its load has not painted yet.`);
                    setCurrentPage(page);
                    setIsPageTransitioning(true);
                    setTimeout(() => setIsPageTransitioning(false), 450);
                    return;
                }
                // RT1 (2026-09-29): the tab's saved-project record is keyed by
                // the verified business, so it cannot be read (QM_STATE_KEY is
                // null) until GET /auth/me confirms it. "Nothing readable" is
                // not "nothing was open": treating it so reset the form and
                // showed an empty project on every reload of /quotemaker. App
                // syncs a reload's address only once the business is
                // confirmed; this is the defence behind that. Stay.
                if (!QM_STATE_KEY) {
                    console.info('[QM] QuoteMaker waits: the saved project cannot be read until the signed-in business is confirmed.');
                    return;
                }
                // A full browser reload starts with empty React state even
                // when this tab has a saved active-project receipt. Restore
                // that exact project before treating the screen as a new
                // quote; the previous fast-path called resetForm(), which
                // deleted the receipt and opened a blank project instead.
                const resumableProjectId = readResumableProjectId();
                if (resumableProjectId) {
                    resumeSavedProject(resumableProjectId);
                    return;
                }
                // Mirror what onNewProject does from the Dashboard - fresh form,
                // Stage 1, import drop zone. resetForm() already sets stage=1
                // and clears sessionStorage[QM_STATE_KEY] so no restore fights us.
                try {
                    resetForm();
                } catch (err) {
                    // A Quote Run refused the reset and has shown its message
                    // (D1). Stay on this page: the form belongs to the run.
                    if (isQuoteRunActiveError(err)) return;
                    throw err;
                }
                setImportPhase('import');
                // Proceed with page transition below but skip the restore block.
                setCurrentPage(page);
                setIsPageTransitioning(true);
                setTimeout(() => setIsPageTransitioning(false), 450);
                // A new project that was never saved and was kept on this
                // device while the server was not answering comes back here: a
                // refresh lands on a blank project with nothing to resume. It
                // is painted over the form reset above (outage lane review).
                Promise.resolve()
                    .then(() => restoreKeptNewProject?.())
                    .catch((err) => {
                        console.warn('[QM] Could not bring back a new project kept on this device:', (err as { message?: string })?.message || err);
                    });
                return;
            }
        }

        // ─── Restore QM view state when returning to QuoteMaker ───────────────
        // If navigating TO quotemaker (and not via prefill), restore the last
        // view state from sessionStorage. This preserves the user's stage,
        // quoteEditorOpen, AND importPhase so they land
        // back exactly where they were - including mid-processing import.
        //
        // IMPORTANT: we unconditionally apply stored values here rather than
        // short-circuiting on `stored.x !== current x`. handleNavigate is a
        // non-memoised closure recreated every render, so the `current` values
        // it captures can drift from the live React state, producing fragile
        // restore behaviour where the stored state wasn't applied because it
        // "looked equal" to the stale closure snapshot. React no-ops setState
        // calls with identical values, so unconditional setters are free.
        //
        // A project load's landing skips it: the load has set the view, and
        // the record can still name the tab's previous job. So does a closure
        // made before a project painted (review 2026-09-30).
        if (page === 'quotemaker' && currentPage !== 'quotemaker' && !options?.prefill && !options?.intakeProjectLoad && !isLoadingProjectRef.current && options?.projectLoadOwnsLanding !== true
            && !projectPaintedSinceMade()) {
            const stored = readQmStateFromStorage();
            if (stored) {
                const quoteGenerationOwnsProject = isQuoteGenerationInFlight();
                if (typeof stored.stage === 'number') {
                    // A stored Stage 5 belongs to the prior quote. Keep the
                    // attended screen on Stage 4 until the active replacement
                    // author saves and opens the current quote itself.
                    setStage(stored.stage === 5 && quoteGenerationOwnsProject ? 4 : stored.stage);
                    setStageDirection('back');
                }
                // A view saved while the old Project Summary page was open (a
                // record written before the merge) restores Stage 1 with the
                // Summary tab. Records written now always say false. A tab
                // request changes no stage and revokes nothing. Only a Stage 1
                // record asks: on another stage the request would wait and be
                // applied on a later, unrelated visit to Stage 1.
                if (stored.summaryViewActive === true && stored.stage === 1) {
                    requestCockpitTab?.('summary');
                }
                // Stage 5 is a virtual stage (stage===5 ⟺ editor open). Re-couple
                // on restore: a saved stage=5 MUST re-open the editor, else the
                // stage strip shows Stage 5 but the modal (gated on quoteEditorOpen)
                // never renders → BLANK. The nav-away editor.reset() (~line 1024)
                // sets quoteEditorOpen=false while stage stays 5, so the session can
                // persist that inconsistent (stage 5 + editor closed) pair — the
                // blank-Stage-5-on-return bug (Peter 2026-06-16). The modal
                // re-hydrates from the preserved quoteData on re-open.
                if (stored.stage === 5 || typeof stored.quoteEditorOpen === 'boolean') {
                    editor.restoreFromSession({
                        open: !quoteGenerationOwnsProject
                            && (stored.stage === 5 || stored.quoteEditorOpen === true),
                    });
                }
                // ★ Restore importPhase. Priority order:
                //   'processing' → always respect (user left mid-import, wants to see sorting floor again)
                //   'form'       → always respect (user was on Import Complete summary)
                //   'import'     → only if there is no existing data; otherwise prefer 'form'
                //                  (a stale 'import' snapshot should not bounce a loaded
                //                  project back to the empty drop zone)
                if (typeof stored.importPhase === 'string') {
                    // 'processing' no longer exists as a separate phase - map it to 'form'
                    // (sorting floor is now embedded inline within the form view)
                    if (stored.importPhase === 'processing' || stored.importPhase === 'form') {
                        setImportPhase('form');
                    } else {
                        const hasExisting = formData.notes || documents.length > 0 || photos.length > 0 || scope.length > 0;
                        setImportPhase(hasExisting ? 'form' : 'import');
                    }
                }
            }
        }

        // Store cross-page navigation options (filter state, scroll targets, etc.)
        // Consumed once by the target page, then cleared on next navigation.
        if (options?.pageNavOptions) {
            setPageNavOptions(options.pageNavOptions);
        } else if (page !== currentPage) {
            setPageNavOptions(null); // clear stale nav options on regular navigation
        }

        const currentIndex = PAGE_ORDER.indexOf(currentPage);
        const newIndex = PAGE_ORDER.indexOf(page);
        const direction = newIndex > currentIndex ? 'forward' : 'back';

        setPageDirection(direction);
        setIsPageTransitioning(true);
        setPrevPage(currentPage);

        // Small delay for exit animation
        setTimeout(() => {
            setCurrentPage(page);
            setTimeout(() => {
                setIsPageTransitioning(false);
            }, 400);
        }, 50);
    };

    const handlePopState = (e: PopStateEvent, skipPageExitGuard = false) => {
        if (!e.state) {
            const path = pageToPath(currentPage) + (window.location.search || '') + (window.location.hash || '');
            try { window.history.pushState(historyViewState({ page: currentPage, stage }), '', path); } catch {}
            return;
        }
        const { page: rawPage, stage: rawTargetStage, summary: rawEntrySummary } = e.state;
        // An entry that names the Summary resolves against what exists now (resolveHistorySummaryTarget): it asks for the
        // Summary tab only while a Summary exists or is being produced.
        const resolveEntrySummary = (resolvedStage: number, honoured: boolean) => resolveHistorySummaryTarget({
            resolvedStage,
            // A stage the readiness gate refused leaves the view where it is; the entry's Summary flag belongs to that stage.
            entrySummary: honoured ? rawEntrySummary : undefined,
            canShowSummary: canShowSummaryView ?? (() => true),
        });
        // Same alias as handleNavigate: a pre-retirement history entry
        // ({ page: 'quotes' }) reached via browser Back must land on the Run
        // Sheet, not resurrect the orphaned Project History page. The address
        // is rewritten too — when currentPage is already 'dashboard' the page
        // sync no-ops and would otherwise leave /quotes in the URL bar.
        const page = rawPage === 'quotes' ? 'dashboard' : rawPage;
        if (rawPage === 'quotes') {
            try { window.history.replaceState({ page: 'dashboard', stage }, '', pageToPath('dashboard')); } catch { /* address cosmetics only */ }
        }
        // X07-002 (Peter 2026-06-21 — "press Back → blank screen" fix): older
        // history entries (and any left over from before v5.41.172 unified the
        // history into one layer) are shaped { qmPage } with NO `page` field.
        // Reading page=undefined here used to run setCurrentPage(undefined)
        // below and render nothing (blank screen — only the bottom nav bar).
        // Ignore those foreign-shaped entries instead of blanking. Guard sits
        // BEFORE isPopstateNav is flipped so a skipped entry never suppresses
        // the next real push.
        if (page == null) return;
        // Intake owns its register/detail entries. No global page or stage
        // changes here: setting isPopstateNav for a same-page detail Back
        // would incorrectly suppress the next real menu navigation's push.
        if (page === 'intake' && currentPage === 'intake') return;
        // Back into QuoteMaker while an "Open in QuoteMaker" is still loading
        // (2026-09-29): the resume below would start a second load against
        // the open, and with nothing saved the branch after it painted an
        // empty QuoteMaker. Stay on this page, put its address back, and let
        // the open land QuoteMaker.
        if (page === 'quotemaker' && page !== currentPage) {
            const pending = openStillLoading();
            if (pending) {
                try { window.history.pushState(historyViewState({ page: currentPage, stage }), '', pageToPath(currentPage)); } catch { /* sandboxed contexts */ }
                sayOpenStillLoading(pending);
                return;
            }
        }
        if (page !== currentPage
            && !skipPageExitGuard
            && requestPageExit?.({
                fromPage: currentPage,
                toPage: page,
                source: 'history',
                proceed: () => handlePopState(e, true),
                stay: () => {
                    try { window.history.pushState(historyViewState({ page: currentPage, stage }), '', pageToPath(currentPage)); } catch { /* sandboxed contexts */ }
                },
            }) === true) return;
        // Every reload (a deploy's service-worker reload, the stale-chunk
        // recovery, signing back in) lands on the address, so the address must
        // name the page this entry shows. The first entry after a reload is
        // seeded with the landing page while the address still names the page
        // that was reloaded: Back into it showed the Run Sheet under
        // /quotemaker, and the next reload opened QuoteMaker (2026-09-29).
        if (!addressNamesPage(window.location.pathname, page)) {
            const entryPath = pageToPath(page) + (window.location.search || '') + (window.location.hash || '');
            try { window.history.replaceState(e.state, '', entryPath); } catch { /* address cosmetics only */ }
        }
        // Browser Back/Forward can restore a Stage 1 entry that asks for the Summary tab.
        // Clear the one-shot continuation before applying that passive state,
        // including same-page stage history entries.
        revokeAttendedAutomaticRiskIntent();
        // Back into QuoteMaker with nothing open (the tab was reloaded on
        // another page, so React state is empty): the branch below would paint
        // an empty Import screen, and the view-state effect used to save it
        // over the tab's saved-project record. Reopen the saved project
        // instead; its load lands the page. Nothing is flagged as a history
        // navigation, so that landing records its own entries. A listener
        // made before a project painted (review 2026-09-30) reads "nothing
        // open" from before that paint, so it shows the project on screen
        // instead of reopening the record over it.
        if (page === 'quotemaker'
            && page !== currentPage
            && !quoteMakerHasActiveWork()
            && !quoteRunJobInProgress?.()
            && !projectPaintedSinceMade()) {
            // Back pressed under the reload's loading screen, before GET
            // /auth/me confirms the business (2026-09-29). The record cannot
            // be read yet, so whether a job was open is not known. Painting
            // QuoteMaker here showed an empty project, and the initial URL
            // sync then found the page already on QuoteMaker and left it.
            // Paint nothing: the address now names QuoteMaker, and that sync,
            // which waits for the confirmation, opens it through
            // handleNavigate, which reopens the saved job.
            if (!QM_STATE_KEY) {
                console.info('[QM] Back into QuoteMaker waits: the saved project cannot be read until the signed-in business is confirmed.');
                return;
            }
            const resumableProjectId = readResumableProjectId();
            if (resumableProjectId) {
                resumeSavedProject(resumableProjectId);
                return;
            }
        }
        // X07-001: Clamp stage to valid range so browser back/forward
        // can't reach invalid stage numbers
        const targetStage = typeof rawTargetStage === 'number'
            ? Math.max(1, Math.min(5, rawTargetStage))
            : stage;

        // isPopstateNav tells App's history-push effect "the view just changed BECAUSE history moved it, the entry already
        // exists, do not push another". It is therefore set only where an entry really changes the view (4C review): it used to be
        // set here for every entry, so a pop that changed nothing (a duplicate or legacy entry for the view on screen, a forward
        // entry the readiness gate refused, a Summary entry that resolved to the Data Entry already showing) left it set, and the
        // next genuine navigation (Next to the Summary) was swallowed as if history had made it.
        if (page !== currentPage) {
            isPopstateNav.current = true;
            // History moved to another page: accepted, so a pending completion
            // loses its navigation authority now, not when the page flips.
            markNavigationAccepted();
            // C16: an open Stage 5 editor leaves the way the menu leaves it (synced, saved once if meaningful, suspended).
            // Without this its overlay stayed above the page history opened and its typed text was never captured.
            if (currentPage === 'quotemaker' && page !== 'quotemaker' && editorIsOpenNow()) leaveQuoteMakerForAnotherPage();
            const currentIndex = PAGE_ORDER.indexOf(currentPage);
            const newIndex = PAGE_ORDER.indexOf(page);
            const direction = newIndex > currentIndex ? 'forward' : 'back';
            setPageDirection(direction);
            setIsPageTransitioning(true);
            setPrevPage(currentPage);
            setTimeout(() => {
                setCurrentPage(page);
                if (page === 'quotemaker') {
                    const resolvedTargetStage = resolveHistoryQuoteStageTarget({
                        currentStage: stage,
                        targetStage,
                        canNavigateToStage: candidate => canNavigateToQuoteStageRef.current(candidate),
                    });
                    // Back into QuoteMaker on an entry written while the old Summary page was open shows Stage 1 with the
                    // Summary tab. Any other entry leaves the tab as it was.
                    const resolvedSummary = resolveEntrySummary(resolvedTargetStage, resolvedTargetStage === targetStage);
                    if (resolvedSummary === true) requestCockpitTab?.('summary');
                    if (resolvedTargetStage !== stage) {
                        if (resolvedTargetStage === 5) {
                            openStage5FromCurrentScope({ transitionToStage5: true });
                        } else {
                            const stageDir = resolvedTargetStage > stage ? 'forward' : 'back';
                            setStageDirection(stageDir);
                            setIsStageTransitioning(true);
                            setPrevStage(stage);
                            setStage(resolvedTargetStage);
                            setTimeout(() => setIsStageTransitioning(false), 350);
                        }
                    } else if (resolvedTargetStage === 5 && !editorIsOpenNow()) {
                        // O-F3: the menu leave suspended the editor but left the internal stage at 5, so the target is not
                        // "different" and nothing opened it: QuoteMaker showed a blank Stage 5. Reopen it from the current scope.
                        openStage5FromCurrentScope({ transitionToStage5: true });
                    }
                }
                setTimeout(() => setIsPageTransitioning(false), 400);
            }, 50);
        } else if (page === 'quotemaker') {
            // X07-001: Apply same data-readiness checks as the stage stepper
            // to prevent browser back/forward from reaching stages with no data.
            // Going backwards is always allowed; going forward requires data.
            const resolvedTargetStage = targetStage !== stage
                ? resolveHistoryQuoteStageTarget({
                    currentStage: stage,
                    targetStage,
                    canNavigateToStage: candidate => canNavigateToQuoteStageRef.current(candidate),
                })
                : stage;
            // Stage 1 has one view. An entry written while the old Summary page was open asks for the Summary tab, which
            // changes no stage, so it is not navigation and accepts nothing.
            const resolvedSummary = resolveEntrySummary(resolvedTargetStage, resolvedTargetStage === targetStage);
            if (resolvedSummary === true) requestCockpitTab?.('summary');
            if (resolvedTargetStage === stage) return;
            // A history entry that actually moves the view is a choice the
            // operator made. One the gate refused or that names the view
            // already on screen returned above and cancels nothing, and marks nothing.
            isPopstateNav.current = true;
            markNavigationAccepted();
            if (resolvedTargetStage === 5) {
                openStage5FromCurrentScope({ transitionToStage5: true });
                return;
            }
            // C16: leaving an open Stage 5 by history is the same exit the pills make. Close it through the modal's receipt
            // boundary (capture the typed text, persist once if the edit is genuine, no review and no learning), and leave the
            // destination to the history entry: a bare close() would also move to Stage 4 and start a second navigation.
            if (editorIsOpenNow() && typeof editor.close === 'function') {
                editor.close({ transitionToStage4: false });
            }
            const goingForward = resolvedTargetStage > stage;
            const direction = goingForward ? 'forward' : 'back';
            setStageDirection(direction);
            setIsStageTransitioning(true);
            setPrevStage(stage);
            setStage(resolvedTargetStage);
            setTimeout(() => setIsStageTransitioning(false), 350);
        }
    };

    return { handleNavigate, handlePopState };
}
